import { useState, useRef, FormEvent, ChangeEvent, useEffect, useMemo, lazy, Suspense, memo } from "react";
import { Link } from "react-router-dom";
import emailjs from 'emailjs-com';
import { 
  Building, 
  FileText, 
  Mail, 
  Phone,
  User,
  Briefcase,
  UserRound,
  BuildingIcon,
  Users,
  ThumbsUp,
  CheckCircle,
  ArrowLeft,
  SendIcon,
  MessageSquare,
  AtSign,
  MessageCircle,
  ChevronRight,
  CheckCircle2,
  ArrowRight,
  Scale,
  Ruler,
  Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Tilt } from "@/components/ui/tilt";
import { Spotlight } from "@/components/ui/spotlight";
import { Helmet } from "react-helmet-async";
import { trackEvent, trackContactFieldInteraction, trackFormAbandonment, trackContactMethodClick, trackFormSubmissionSuccess, CONTACT_EVENTS, CONTACT_METHODS } from "@/utils/analytics";
import { useAnalyticsTracker } from "@/hooks/useAnalyticsTracker";
import ErrorBoundary from '@/components/ErrorBoundary';

type TestimonialItem = {
  id: number;
  name: string;
  company: string;
  email: string;
  quote: string;
  showEmail: boolean;
};

const FeaturesSectionWithHoverEffects = lazy(() => 
  import('@/components/blocks/feature-section-with-hover-effects')
);

const Index = () => {
  const [testimonials, setTestimonials] = useState<TestimonialItem[]>([
    {
      id: 1,
      name: "Manuel Nunes Baptista",
      company: "Swiss Steel Brasil",
      email: "manuel.baptista@swisssteel.com.br",
      quote: "O acompanhamento da obra e o conhecimento técnico do Moacyr foram fundamentais para o sucesso na demanda contra o empreiteiro contratado. Sua experiência e profissionalismo fizeram toda a diferença.",
      showEmail: false,
    },
    {
      id: 2,
      name: "Fabio Sanches de Araujo",
      company: "Araújo Advogados Associados",
      email: "fabio@araujoadvogados.adv.br",
      quote: "Como advogado, contar com a parceria do Moacyr como Assistente Técnico foi decisivo para o sucesso em causas que defendemos. Recomendamos o Moacyr para diversos clientes pela sua acertividade na elaboração de laudos técnicos precisos e bem fundamentados.",
      showEmail: false,
    },
    {
      id: 3,
      name: "Antonio Carlos Ortolano",
      company: "Consultores",
      email: "ortolanoac@gmail.com",
      quote: "Como consultor finaceiro de empresas, posso afirmar que o trabalho do Moacyr em avaliações imobiliárias e apoio técnico em demandas judiciais são exemplares. Recomendo o Moacyr pelo seu comprometimento, capacidade de análise e precisão técnica.",
      showEmail: false,
    }
  ]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [formStartTime, setFormStartTime] = useState<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const { trackUserInteraction } = useAnalyticsTracker();

  useEffect(() => {
    if (formRef.current && !formStartTime) {
      const handleFormInteraction = () => {
        setFormStartTime(Date.now());
        trackEvent('ContactForm', CONTACT_EVENTS.FORM_START, 'Form interaction started');
      };

      formRef.current.addEventListener('focusin', handleFormInteraction);
      return () => {
        formRef.current?.removeEventListener('focusin', handleFormInteraction);
      };
    }
  }, [formStartTime]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (formStartTime && !isSubmitted) {
        const timeSpent = Date.now() - formStartTime;
        trackFormAbandonment(formData, timeSpent);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formStartTime, isSubmitted, formData]);

  const toggleEmailVisibility = (id: number) => {
    setTestimonials(prev => 
      prev.map(t => 
        t.id === id ? { ...t, showEmail: !t.showEmail } : t
      )
    );
    trackEvent('Testimonials', 'ToggleEmail', `Testimonial ID: ${id}`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    trackContactFieldInteraction(name, 'change', value, name === 'email' ? validateEmail(value) : true);
    
    if (name === 'email') {
      setEmailError('');
    }
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name } = e.target;
    trackContactFieldInteraction(name, 'focus');
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    trackContactFieldInteraction(name, 'blur', value, name === 'email' ? validateEmail(value) : true);
  };

  const handleBackToForm = () => {
    trackEvent('ContactForm', 'ResetForm', 'Return to empty form');
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const obfuscateEmail = (email: string) => {
    return email;
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push("Nome é obrigatório");
    if (!formData.email.trim()) errors.push("Email é obrigatório");
    if (!validateEmail(formData.email)) errors.push("Email inválido");
    if (!formData.message.trim()) errors.push("Mensagem é obrigatória");

    if (errors.length > 0) {
      trackEvent('FormValidation', 'Errors', errors.join(', '));
      toast.error(`Erros encontrados:\n${errors.join('\n')}`);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        to_email: 'rafa.ferreira@gmail.com',
        subject: formData.subject || 'Contato via Site',
        message: formData.message
      };

      await emailjs.send(
        'service_spr1ggv',
        'template_19yj66g',
        templateParams,
        '_m_MDC3y8ocLk7Ytf'
      );

      const timeSpent = formStartTime ? Date.now() - formStartTime : 0;
      trackFormSubmissionSuccess(formData, timeSpent);
      
      toast.success("Mensagem enviada com sucesso!");
      setIsSubmitted(true);
    } catch (error: any) {
      console.error('EmailJS Error:', error);
      trackEvent('EmailJS', 'SendError', error.message || 'Unknown error');
      
      if (error.status === 400) {
        toast.error("Formulário inválido. Verifique os dados inseridos.");
      } else if (error.status === 500) {
        toast.error("Erro no servidor. Tente novamente mais tarde.");
      } else {
        toast.error("Erro ao enviar mensagem. Tente novamente.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderConfirmationPage = () => {
    return (
      <div className="py-16 px-4 max-w-2xl mx-auto text-center">
        <div className="bg-gradient-to-br from-blue-900/60 to-blue-800/40 rounded-xl p-8 backdrop-blur-md shadow-lg border border-blue-500/20 animate-scale-in">
          <div className="flex justify-center mb-8">
            <div className="rounded-full bg-green-500/30 p-6 ring-4 ring-green-400/20">
              <ThumbsUp className="h-16 w-16 text-green-400 animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-blue-400">Mensagem Enviada!</h2>
          <div className="flex items-center justify-center mb-8">
            <CheckCircle className="h-6 w-6 text-green-400 mr-3" />
            <p className="text-blue-100 text-lg">Sua mensagem foi processada com sucesso</p>
          </div>
          <p className="text-gray-300 mb-10 text-lg">
            Sua mensagem foi enviada com sucesso, aguarde máximo 3 dias úteis que retornaremos.
          </p>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-base h-auto rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/30"
            onClick={handleBackToForm}
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Voltar para o Formulário
          </Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          trackEvent('Section', 'View', `Section: ${entry.target.id}`);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    const sections = ['home', 'services', 'about', 'testimonials', 'contact'];
    sections.forEach(sectionId => {
      const section = document.getElementById(sectionId);
      if (section) observer.observe(section);
    });

    return () => {
      sections.forEach(sectionId => {
        const section = document.getElementById(sectionId);
        if (section) observer.unobserve(section);
      });
    };
  }, []);

  interface TestimonialProps {
    testimonial: TestimonialItem;
  }

  const MemoizedTestimonial = memo(({ testimonial }: TestimonialProps) => {
    const shortName = testimonial.name.split(' ').filter((part, index, arr) => 
      index === 0 || index === arr.length - 1
    ).join(' ');
    
    return (
      <div 
        key={testimonial.id} 
        className="card-light rounded-lg p-8 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-500/10 transition-all duration-300 flex flex-col h-full"
        onMouseEnter={() => trackEvent('Testimonials', 'Hover', `Testimonial: ${testimonial.name}`)}
      >
        <div className="flex flex-col h-full">
          <div className="mb-4">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg key={star} className="w-5 h-5 text-yellow-500 dark:text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                </svg>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <p className="text-blue-600 dark:text-blue-400 font-medium">{testimonial.company}</p>
          </div>
          <div className="flex-grow">
            <p className="text-gray-700 dark:text-gray-300 italic mb-4 text-left">
              "{testimonial.quote}"
            </p>
          </div>
          <div className="border-t border-themed pt-4 mt-auto">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className="text-left">
                  <h3 className="font-semibold text-themed">{shortName}</h3>
                </div>
              </div>
              
              {testimonial.showEmail ? (
                <a href={`mailto:${testimonial.email}`} className="text-sm text-blue-600 dark:text-blue-400 truncate max-w-[150px]" title={testimonial.email}>
                  {testimonial.email}
                </a>
              ) : (
                <button 
                  onClick={() => toggleEmailVisibility(testimonial.id)}
                  className="px-3 py-1 rounded-md text-sm bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/60 transition-colors flex items-center space-x-1"
                  aria-label="Mostrar email"
                >
                  <Mail className="w-4 h-4" />
                  <span>Email</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  });

  useEffect(() => {
    import('emailjs-com').then(({ default: emailjs }) => {
      // Use emailjs
    });
  }, []);

  useEffect(() => {
    const loadScript = () => {
      const script = document.createElement('script');
      script.src = 'https://example.com/analytics.js';
      script.async = true;
      document.body.appendChild(script);
    };

    window.addEventListener('load', loadScript);
    return () => window.removeEventListener('load', loadScript);
  }, []);

  return (
    <>
      <Helmet>
        <title>Perito Judicial em Campinas | Eng. Moacyr Moura</title>
        <meta name="description" content="Engenheiro Civil especializado em perícia judicial e assistência técnica em Campinas. Vistorias técnicas, avaliações imobiliárias e laudos periciais com excelência e credibilidade." />
        <meta name="keywords" content="perito judicial, assistente técnico, engenharia civil, laudos técnicos, vistorias, avaliações imobiliárias, perícia judicial, São Paulo" />
        
        <meta property="og:title" content="Perito Judicial em Campinas | Eng. Moacyr Moura" />
        <meta property="og:description" content="Engenheiro Civil especializado em perícia judicial e assistência técnica em Campinas. Vistorias técnicas, avaliações imobiliárias e laudos periciais com excelência e credibilidade." />
        <meta property="og:image" content="https://www.periciatecnica.eng.br/assets/images/og/og-image.png" />
        <meta property="og:url" content="https://www.periciatecnica.eng.br" />
        <meta property="og:type" content="website" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Perito Judicial em Campinas | Eng. Moacyr Moura" />
        <meta name="twitter:description" content="Engenheiro Civil especializado em perícia judicial e assistência técnica em Campinas. Vistorias técnicas, avaliações imobiliárias e laudos periciais com excelência e credibilidade." />
        <meta name="twitter:image" content="https://www.periciatecnica.eng.br/assets/images/og/og-image.png" />
        
        <link rel="canonical" href="https://www.periciatecnica.eng.br" />
        
        {/* Structured data for rich search results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": "https://www.periciatecnica.eng.br",
            "name": "Perícia Técnica - Eng. Moacyr Moura",
            "image": "https://www.periciatecnica.eng.br/assets/images/team/moacyr.jpg",
            "logo": "https://www.periciatecnica.eng.br/logo.png",
            "url": "https://www.periciatecnica.eng.br",
            "telephone": "+5519991133163",
            "email": "moacyr.mmf@gmail.com",
            "description": "Engenheiro Civil especializado em perícia judicial e assistência técnica. Vistorias técnicas, avaliações imobiliárias e laudos periciais.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Campinas",
              "addressRegion": "SP",
              "addressCountry": "BR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": -22.9064,
              "longitude": -47.0616
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"
              ],
              "opens": "09:00",
              "closes": "18:00"
            },
            "sameAs": [
              "https://www.linkedin.com/in/moacyr-moura/",
              "https://wa.me/5519991133163"
            ],
            "priceRange": "$$",
            "serviceArea": {
              "@type": "GeoCircle",
              "geoMidpoint": {
                "@type": "GeoCoordinates",
                "latitude": -22.9064,
                "longitude": -47.0616
              },
              "geoRadius": "100000"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Perícia Técnica - Eng. Moacyr Moura",
            "mainEntityOfPage": "https://www.periciatecnica.eng.br",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.periciatecnica.eng.br/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            },
            "siteLinks": [
              {
                "@type": "SiteLinksSearchBox",
                "url": "https://www.periciatecnica.eng.br"
              }
            ]
          })}
        </script>
        
        {/* Navigation schema for sitelinks in search results */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "itemListElement": [
              {
                "@type": "SiteNavigationElement",
                "position": 1,
                "name": "Serviços",
                "description": "Serviços de perícia técnica e assistência judicial",
                "url": "https://www.periciatecnica.eng.br/#services"
              },
              {
                "@type": "SiteNavigationElement",
                "position": 2,
                "name": "Sobre",
                "description": "Sobre o Eng. Moacyr Moura e sua experiência",
                "url": "https://www.periciatecnica.eng.br/#about"
              },
              {
                "@type": "SiteNavigationElement",
                "position": 3,
                "name": "Referências",
                "description": "Depoimentos de clientes",
                "url": "https://www.periciatecnica.eng.br/#testimonials"
              },
              {
                "@type": "SiteNavigationElement",
                "position": 4,
                "name": "Contato",
                "description": "Entre em contato para consultas e orçamentos",
                "url": "https://www.periciatecnica.eng.br/#contact"
              },
              {
                "@type": "SiteNavigationElement",
                "position": 5,
                "name": "Blog",
                "description": "Artigos sobre perícia técnica e engenharia",
                "url": "https://www.periciatecnica.eng.br/blog"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <section id="home" className="pt-32 lg:pt-40 pb-24 lg:pb-36 relative overflow-hidden bg-themed-primary">
        <div className="absolute inset-0 z-0 section-bg-light">
          <div className="absolute inset-0 bg-white/70 dark:bg-black/50 backdrop-blur-sm"></div>
        </div>
        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white leading-tight animate-scale-in">
              Perito Judicial ou Assistente Técnico?{" "}
              <span className="text-blue-600 dark:text-blue-400">
                Descubra Quem Você Precisa Para Defender Seu Caso
              </span>
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Engenheiro Civil Sênior com Certificação IBAPE/SP oferece laudos, vistorias documentadas e apoio técnico em todo processo judicial.
            </p>
            <div className="flex justify-center gap-4 pt-6">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-6 text-lg h-auto"
                onClick={() => {
                  trackEvent('Navigation', 'CTAClick', 'Hero CTA - Contact Form');
                  const element = document.querySelector("#contact");
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Entre em Contato para Saber Mais!
              </Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-gray-50 dark:from-black to-transparent"></div>
      </section>

      <section id="services" className="py-16 bg-themed-secondary section-zebra-light relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-white/70 dark:bg-black/50"></div>
        </div>
        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white">
            Nossos Serviços
          </h2>
          <ErrorBoundary fallback={<div>Failed to load component</div>}>
            <Suspense fallback={<div>Loading...</div>}>
              <FeaturesSectionWithHoverEffects />
            </Suspense>
          </ErrorBoundary>
        </div>
      </section>

      <section id="about" className="py-16 bg-themed-primary section-zebra-dark">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white">
            Conheça Moacyr
          </h2>
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="flex flex-col items-start space-y-6">
              <div className="w-64 h-64 relative mx-auto lg:mx-0">
                <Tilt 
                  rotationFactor={8} 
                  isRevese 
                  className="rounded-full"
                  springOptions={{
                    stiffness: 26.7,
                    damping: 4.1,
                    mass: 0.2,
                  }}
                >
                  <Spotlight
                    className="z-10 from-blue-400/30 via-blue-300/20 to-blue-200/10 blur-2xl"
                    size={248}
                  />
                  <img 
                    src="/assets/images/team/moacyr.jpg"
                    alt="Moacyr Moura Ferreira"
                    className="w-full h-full object-cover rounded-full border-4 border-gray-200 dark:border-white/10 group-hover:border-blue-400/30 transition-all duration-300"
                    loading="lazy"
                    width="256"
                    height="256"
                  />
                </Tilt>
              </div>
              <h3 className="text-2xl font-semibold w-full lg:text-left text-center text-themed">Moacyr Moura Ferreira</h3>
              <div className="space-y-4 text-gray-700 dark:text-gray-300 text-left">
                <p>Graduado Engenheiro Civil pela Escola de Engenharia de Lins (UNILINS)</p>
                <p>Atuação como Assistente Técnico atendendo a clientes diversos.</p>
                <p>Gerenciamento de projetos (Arquitetura, Estruturas e Instalações diversas), notadamente industriais e comerciais, para empresas nacionais e multinacionais, com atuação nos Estados de São Paulo e Minas Gerais e também no exterior (USA).</p>
                <p>Engenheiro contratado em empresas diversas, nacionais e multinacionais, evoluindo desde engenheiro residente de obras até diretoria executiva.</p>
              </div>
            </div>

            <div className="card-light rounded-lg p-8">
              <h3 className="text-2xl font-semibold mb-6 text-left text-themed">Por que Escolher Me</h3>
              <div className="space-y-4 text-left">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Perícia Judicial e Avaliação de Imóveis pela Academia do Perito - Fernando Sarian (2023)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Assistente Técnico Judicial pela Academia do Perito - Fernando Sarian (2023)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Avaliação de Aluguéis pelo IBAPE - Instituto Brasileiro de Avaliações e Perícias de Engenharia (2022)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Avaliação de Benfeitorias Urbanas - Workshop IBAPE (2022)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Patologia em Edificações - Visão Geral, Garantias e Assistência Técnica - Palestras AECweb (2023)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Patologia em Edificações - Fundações e Estruturas de Concreto - Palestras AECweb (2023)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Patologia em Edificações - Alvenarias e Revestimento de Fachadas - Palestras AECweb (2023)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Patologia em Edificações - Sistemas Hidrosanitários e Falhas de Impermeabilização - Palestras AECweb (2023)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Administração de Empresas - Convênio CAPI/MEC (2021)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Administração Financeira - IDORT, SP (2021)</span>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full border border-blue-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                  </div>
                  <span className="text-gray-700 dark:text-gray-300">Gerenciamento de Construções - Productivity/CATHO, SP (2021)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="service-area" className="py-16 bg-themed-secondary section-zebra-light">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white">
            Atendemos clientes todos os municípios distantes até 100 KM de Campinas
          </h2>
          <div className="flex justify-center">
            <div className="w-full max-w-xl aspect-square relative">
              <Tilt 
                rotationFactor={5} 
                isRevese 
                className="rounded-full overflow-hidden border-4 border-blue-400/30"
                springOptions={{
                  stiffness: 26.7,
                  damping: 4.1,
                  mass: 0.2,
                }}
              >
                <Spotlight
                  className="z-10 from-blue-400/30 via-blue-300/20 to-blue-200/10 blur-2xl"
                  size={400}
                />
                <picture>
                  <source srcSet="/assets/images/maps/optimized/area-cobertura.webp" type="image/webp" />
                  <img 
                    src="/assets/images/maps/optimized/area-cobertura.jpg"
                    alt="Mapa da área de atendimento"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="400"
                    height="400"
                  />
                </picture>
                <div className="absolute inset-0 shadow-inner rounded-full border border-blue-400/10"></div>
              </Tilt>
            </div>
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-16 bg-themed-primary section-zebra-dark">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-3xl lg:text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white">
            Referências
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.filter(t => t.quote !== "[Aguardando depoimento]").map((testimonial) => (
              <MemoizedTestimonial key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="py-16 bg-themed-secondary section-zebra-light">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-center mb-8 text-gray-900 dark:text-white">
              Entre em Contato
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg max-w-2xl mx-auto">Estamos ansiosos para ouvir você. Preencha o formulário abaixo e entraremos em contato em breve.</p>
          </div>

          <div className="max-w-3xl mx-auto">
            {isSubmitted ? (
              renderConfirmationPage()
            ) : (
              <div className="card-light rounded-xl p-8 lg:p-10 transform transition-all duration-500 hover:shadow-blue-500/5">
                <form className="space-y-8" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label htmlFor="name" className="flex items-center text-gray-800 dark:text-gray-300 font-medium text-left">
                        <User className="h-4 w-4 mr-2 text-blue-500" />
                        Nome *
                      </label>
                      <div className="relative group">
                        <input
                          type="text"
                          id="name"
                          name="name"
                          placeholder="Seu nome"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 placeholder:text-gray-500 py-6 pl-4 pr-4 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-300 group-hover:border-gray-400 dark:group-hover:border-gray-500 w-full" 
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="email" className="flex items-center text-gray-800 dark:text-gray-300 font-medium text-left">
                        <AtSign className="h-4 w-4 mr-2 text-blue-500" />
                        Email *
                      </label>
                      <div className="relative group">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="seu.email@exemplo.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 placeholder:text-gray-500 py-6 pl-4 pr-4 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-300 group-hover:border-gray-400 dark:group-hover:border-gray-500 w-full ${emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                          required
                        />
                        {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="subject" className="flex items-center text-gray-800 dark:text-gray-300 font-medium text-left">
                      <FileText className="h-4 w-4 mr-2 text-blue-500" />
                      Assunto
                    </label>
                    <div className="relative group">
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        placeholder="Assunto da mensagem"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 placeholder:text-gray-500 py-6 pl-4 pr-4 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-300 group-hover:border-gray-400 dark:group-hover:border-gray-500 w-full" 
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="message" className="flex items-center text-gray-800 dark:text-gray-300 font-medium text-left">
                      <MessageSquare className="h-4 w-4 mr-2 text-blue-500" />
                      Mensagem *
                    </label>
                    <div className="relative group">
                      <textarea
                        id="message"
                        name="message"
                        placeholder="Sua mensagem"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="min-h-[180px] bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-300 placeholder:text-gray-500 py-4 px-4 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all duration-300 group-hover:border-gray-400 dark:group-hover:border-gray-500 resize-none w-full" 
                        required
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-4 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-600/30 flex items-center justify-center"
                    disabled={isSubmitting}
                    onClick={() => {
                      if (!isSubmitting) {
                        trackEvent('ContactForm', 'SubmitClick', 'Submit button clicked');
                      }
                    }}
                    aria-label="Enviar formulário de contato"
                    aria-disabled={isSubmitting}
                    tabIndex={0}
                    role="button"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enviando...
                      </>
                    ) : "Enviar Mensagem"}
                  </Button>
                  
                  <div className="pt-2 border-t border-gray-300 dark:border-gray-700">
                    <p className="text-gray-600 dark:text-gray-500 text-sm text-center">
                      * campos obrigatórios
                    </p>
                  </div>
                </form>
                
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <a 
                    href="mailto:moacyr.mmf@gmail.com" 
                    className="flex items-center space-x-4 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg border border-gray-300 dark:border-gray-600/50 hover:border-blue-500/30 hover:bg-gray-200 dark:hover:bg-gray-600/50 transition-all duration-300 group"
                    onClick={trackUserInteraction('Contact', 'Click', 'Email')}
                  >
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-3 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/50 transition-all duration-300">
                      <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
                    </div>
                    <span className="text-gray-800 dark:text-gray-300 text-left group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-all duration-300">moacyr.mmf@gmail.com</span>
                  </a>
                  <a 
                    href="https://wa.me/5519991133163?text=Ola%20gostaria%20de%20maiores%20informacoes%2C%20eu%20vim%20do%20seu%20site%20periciatecnic" 
                    className="flex items-center space-x-4 bg-gray-100 dark:bg-gray-700 p-4 rounded-lg border border-gray-300 dark:border-gray-600/50 hover:border-blue-500/30 hover:bg-gray-200 dark:hover:bg-gray-600/50 transition-all duration-300 group"
                    onClick={trackUserInteraction('Contact', 'Click', 'WhatsApp')}
                  >
                    <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-3 group-hover:bg-blue-200 dark:group-hover:bg-blue-800/50 transition-all duration-300">
                      <MessageCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300" />
                    </div>
                    <span className="text-gray-800 dark:text-gray-300 text-left group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-all duration-300">WhatsApp</span>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default Index;
